import React, { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import "./OtherPurchaseOptions.css";
import PaymentSummaryModal from "../../Modals/paymentSummaryModal/PaymentSummaryModal";
import { useAuth } from "../../context/auth";
import { TicketIcon, AttireIcon, GiftIcon } from "../../helper/icon";
import { formatDateLessThanOneDay } from "../../helper/utils";
import AttireModal from "../../Modals/attireModal/AttireModal";
import RefundConfirmModal from "../../Modals/refundConfirmModal/RefundConfirmModal";
import RefundBankModal from "../../Modals/refundBankModal/RefundBankModal";
import RefundAttireModal from "../../Modals/refundAttireModal/RefundAttireModal";
import RefundBreakdownModal from "../../Modals/refundBreakDownModal/RefundBreakdownModal";
import RefundSuccessModal from "../../Modals/refundSuccessModal/RefundSuccessModal";
import RefundFailedModal from "../../Modals/refundFailedModal/RefundFailedModal";

const OtherPurchaseOptions = ({
  event,
  onPurchaseClick,
  isOpen,
  invitation,
}) => {
  const location = useLocation();
  const eventData = location.state?.eventDetails || event || {};
  const { user } = useAuth();

  const hasPaidForAttire = invitation?.eventAttire?.some(
    (attire) => attire.paid
  );
  const hasPaidForTicket = invitation?.ticketFeePaid?.some(
    (ticket) => ticket.paid
  );
  const hasPaidCashGift = invitation?.cashGiftPaid?.giftAmountPaid > 0;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  let hasValidEventDate = false;

  if (event?.eventDate) {
    const eventDate = new Date(event.eventDate);
    eventDate.setHours(0, 0, 0, 0);

    hasValidEventDate = eventDate >= today;
  } else if (event?.startDate && event?.endDate) {
    const endDate = new Date(event.endDate);
    endDate.setHours(23, 59, 59, 999); // end of day

    hasValidEventDate = endDate >= new Date();
  } else if (event?.multipleDates?.length) {
    hasValidEventDate = event.multipleDates.some((d) => {
      const date = new Date(d.date);
      date.setHours(0, 0, 0, 0);
      return date >= today;
    });
  }
  const paidTicket =
    invitation?.ticketFeePaid?.length > 0 ? invitation.ticketFeePaid[0] : null;
  const paidCashGift = invitation?.cashGiftPaid;
  const attireRefunds = eventData?.attireRefunds || [];
  const attireOptions = eventData?.eventAttire?.asoEbiImages || [];
  const hasRefundPolicy = !!eventData?.eventAttire?.refundDeadlineDate;
  const showRefundButton = hasPaidForAttire && hasRefundPolicy;

  const [refundStatus, setRefundStatus] = useState(null);
  const [refundStep, setRefundStep] = useState(null);
  const [refundError, setRefundError] = useState(null);
  const [isSubmittingRefund, setIsSubmittingRefund] = useState(false);
  const [refundQuantities, setRefundQuantities] = useState({});
  const [refundRequestDate, setRefundRequestDate] = useState(null);
  const [guestRefundDetails, setGuestRefundDetails] = useState(null);
  const [refundPayload, setRefundPayload] = useState({
    bank: null,
    items: null,
  });

  const [modalType, setModalType] = useState(null);
  const [cashGiftAmount, setCashGiftAmount] = useState("");
  const [banks, setBanks] = useState([]);
  const [banksLoading, setBanksLoading] = useState(true);

  const [isPaymentSummaryModalOpen, setIsPaymentSummaryModalOpen] =
    useState(false);
  const [paymentSummaryData, setPaymentSummaryData] = useState(null);
  useEffect(() => {
    const fetchBanks = async () => {
      try {
        const response = await axios.get("/paystack/bank");
        setBanks(Array.isArray(response.data?.data) ? response.data.data : []);
      } catch {
        setBanks([]);
      } finally {
        setBanksLoading(false);
      }
    };

    fetchBanks();
  }, []);

  useEffect(() => {
    if (!invitation || !user?._id) return;

    const attirePaid = invitation.eventAttire?.some((a) => a.paid) ?? false;

    if (attireRefunds?.length > 0) {
      const guestRefund = attireRefunds.find(
        (refund) => refund.guestId === user._id
      );
      if (guestRefund) {
        setRefundStatus(guestRefund.refundStatus);
        setRefundRequestDate(guestRefund.requestDate);

        const refundedAttire = guestRefund.eventAttire?.map((item) => ({
          ...item,
          refunded: guestRefund.refundStatus === "accepted",
        }));

        setGuestRefundDetails({
          status: guestRefund.refundStatus,
          requestDate: guestRefund.requestDate,
          responseDate: guestRefund.responseDate,
          bankDetails: guestRefund.bankDetails,
          attireItems: refundedAttire,
        });
      }
    }
  }, [invitation, user?._id]);

  const openModal = (type) => {
    setModalType(type);
    setCashGiftAmount("");
  };

  const closeModal = () => {
    setModalType(null);
  };

  const formatCurrency = (amount) => {
    const num = typeof amount === "string" ? parseFloat(amount) : amount;

    if (num === 0) {
      return "Free";
    }

    const currency = eventData?.preferredCurrency || "NGN";
    const symbol = currency === "NGN" ? "₦" : "$";
    return `${symbol}${num.toLocaleString()}`;
  };

  const normalizedRefundStatus =
    typeof refundStatus === "string" && refundStatus.trim()
      ? refundStatus
      : null;

  const buildEventAttirePayload = () => {
    return invitation.eventAttire
      .filter((attire) => refundQuantities[attire._id])
      .map((attire) => {
        const quantity = refundQuantities[attire._id];

        return {
          uri: attire.uri,
          price: attire.price,
          quantity,
          maxQty: attire.quantity,
          amountPaid: attire.price * quantity,
          amountToRefund: attire.price * quantity,
          attireTag: attire.attireTag,
        };
      });
  };

  const handleRefundRequest = async () => {
    setIsSubmittingRefund(true);

    const payload = {
      bankDetails: refundPayload.bank,
      eventAttire: buildEventAttirePayload(),
      currency: eventData?.preferredCurrency || "NGN",
    };

    try {
      if (
        !payload?.eventAttire ||
        Object.keys(payload.eventAttire).length === 0
      ) {
      }

      if (
        payload.currency === "NGN" &&
        (!payload.bankDetails || !payload.bankDetails.bank)
      ) {
        // throw new Error("Bank details are required for NGN refunds");
      }

      const response = await axios.patch(
        `/${eventData._id}/attire-refunds-request/${user._id}`,
        payload
      );

      if (!response.data?.success) {
        setRefundError(
          response.data?.message || "Refund request could not be processed"
        );
        setRefundStep("failed");
        return;
      }

      setRefundStatus("pending");
      setRefundStep("success");
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.message ||
        "Something went wrong while submitting your refund request";

      setRefundError(message);
      setRefundStep("failed");
    } finally {
      setIsSubmittingRefund(false);
    }
  };

  const CashGiftModal = ({ onProceed }) => {
    const [localCashAmount, setLocalCashAmount] = useState("");
    const [localDescription, setLocalDescription] = useState("");
    const [localFormattedValue, setLocalFormattedValue] = useState("");

    const handleProceed = (e) => {
      e.preventDefault();
      e.stopPropagation();

      const cashGiftData = {
        type: "cashGift",
        selectedOptions: [
          {
            type: "Cash Gift",
            price: parseFloat(localCashAmount),
            description: localDescription || "Cash Gift Donation",
          },
        ],
        quantities: { "Cash Gift": 1 },
        deliveryAddress: null,
        isAttirePurchase: false,
        applyServiceCharge: false,
      };

      setPaymentSummaryData(cashGiftData);
      setIsPaymentSummaryModalOpen(true);
    };

    const handleCashGiftAmountChange = (e) => {
      e.stopPropagation();
      const value = e.target.value.replace(/[^0-9.]/g, "");
      setLocalCashAmount(value);
      setLocalFormattedValue(value);
    };

    const handleAmountBlur = (e) => {
      e.stopPropagation();
      if (localCashAmount) {
        setLocalFormattedValue(formatCurrency(localCashAmount));
      }
    };

    const handleAmountFocus = (e) => {
      e.stopPropagation();
      setLocalFormattedValue(localCashAmount);
    };

    const handleModalClick = (e) => {
      e.stopPropagation();
    };

    return (
      <div className="other-purchase-modal-overlay" onClick={closeModal}>
        <div
          className="other-purchase-modal-content"
          onClick={handleModalClick}
        >
          <button
            onClick={closeModal}
            className="modal-close-button"
            type="button"
          >
            <span>×</span>
          </button>
          <div className="modal-body">
            <h2 className="purchase-title">Donate Cash Gift</h2>
            <p className="purchase-description">
              Enter the amount you wish to donate to support the event or host.
            </p>
            <form onSubmit={handleProceed}>
              <div className="cash-gift-input-container">
                <label className="cash-gift-label">Amount</label>
                <div className="amount-input-container">
                  <span className="amount-label prefix">
                    {eventData?.preferredCurrency === "USD" ? "USD" : "NGN"}
                  </span>
                  <input
                    type="text"
                    placeholder="Enter amount"
                    value={localFormattedValue}
                    onChange={handleCashGiftAmountChange}
                    onBlur={handleAmountBlur}
                    onFocus={handleAmountFocus}
                    className="cash-gift-input"
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
                <div className="description-input-container">
                  <label className="description-label">
                    Description (Optional)
                  </label>
                  <textarea
                    className="description-input"
                    placeholder="Enter a description..."
                    value={localDescription}
                    onChange={(e) => {
                      e.stopPropagation();
                      setLocalDescription(e.target.value);
                    }}
                    onClick={(e) => e.stopPropagation()}
                    rows={5}
                    cols={30}
                  />
                </div>
              </div>
              <div className="modal-controls">
                <button
                  type="button"
                  onClick={closeModal}
                  className="modal-button other-purchase-cancel"
                >
                  Close
                </button>
                <button
                  type="submit"
                  className="modal-button proceed-button"
                  disabled={
                    !localCashAmount || parseFloat(localCashAmount) <= 0
                  }
                >
                  Proceed
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  };

  const PurchaseCard = ({
    type,
    item,
    amount,
    datePaid,
    deliveryAddress,
    icon,
    refundItem = null,
    giftDescription,
    attirePaymentdetails = null,
  }) => (
    <div className="purchase-detail-card">
      <div className="purchase-card-icon">{icon}</div>

      <div className="purchase-card-details">
        <div className="purchase-card-name">{item}</div>

        <div className="purchase-row">
          <span className="label">Amount</span>
          <span className="value">
            <strong>
              {formatCurrency(
                (amount || 0) - (refundItem?.amountToRefund || 0)
              )}
            </strong>{" "}
          </span>
        </div>

        {giftDescription && (
          <div className="purchase-row">
            <span className="label">Desc.</span>
            <span className="value">
              {giftDescription.charAt(0).toUpperCase() +
                giftDescription.slice(1)}
            </span>
          </div>
        )}

        {attirePaymentdetails && (
          <div className="purchase-row">
            <span className="label">Paid</span>
            <span className="value">
              {attirePaymentdetails} = {formatCurrency(amount)}
            </span>
          </div>
        )}

        {datePaid && (
          <div className="purchase-row">
            <span className="label">Date Paid</span>
            <span className="value">{formatDateLessThanOneDay(datePaid)}</span>
          </div>
        )}

        {refundItem && (
          <div className="purchase-row">
            <span className="label">Refund</span>
            <span className="value">
              {formatCurrency(refundItem.price)} × {refundItem.quantity} =
              {formatCurrency(refundItem.amountToRefund)}
            </span>
          </div>
        )}

        {deliveryAddress && (
          <div className="purchase-row">
            <span className="label">Delivery Add.</span>
            {deliveryAddress.charAt(0).toUpperCase() + deliveryAddress.slice(1)}
          </div>
        )}
      </div>
    </div>
  );

  // const today = new Date();

  const refundDeadlineDate = eventData?.eventAttire?.refundDeadlineDate
    ? new Date(eventData.eventAttire.refundDeadlineDate)
    : null;

  if (refundDeadlineDate) {
    refundDeadlineDate.setHours(23, 59, 59, 999);
  }

  const isRefundDeadlineActive =
    refundDeadlineDate && refundDeadlineDate >= today;

  return (
    <>
      <div className="other-modal-content">
        <div className="other-modal-body">
          <div className="other-options-container">
            {(!hasPaidForAttire && eventData.isEventAttireCompulsory) ||
            (!hasPaidForTicket && eventData.isEntryFeeApplicable) ? (
              <div className="purchase-info-section">
                <div className="info-icon">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"
                      fill="currentColor"
                    />
                  </svg>
                </div>
                <div className="info-text">
                  <p>
                    Items marked with an asterisk (
                    <span className="required">*</span>) are mandatory for event
                    entry. Failure to complete the required purchase may result
                    in your invite being revoked.
                  </p>
                  <p className="sub-info">
                    Optional items can be purchased at any time to elevate your
                    event experience.
                  </p>
                </div>
              </div>
            ) : null}

            {eventData.isEventAttireAvailable && (
              <div className="other-option-row">
                <div className="other-option-description">
                  <div className="option-title-wrapper">
                    <h3 className="other-option-title">
                      Event Attire
                      {eventData.isEventAttireCompulsory && (
                        <span className="required">*</span>
                      )}
                    </h3>
                  </div>
                  <p className="other-option-text">
                    Event Attire Available! Aso Ebi, Fila, Customized T-Shirt,
                    Caps, etc
                  </p>

                  {hasPaidForAttire &&
                    invitation.eventAttire?.length > 0 &&
                    invitation.eventAttire.map((attire) => {
                      const matchingRefund = attireRefunds
                        ?.flatMap((r) => r.eventAttire || [])
                        ?.find(
                          (ref) =>
                            ref.attireTag === attire.attireTag &&
                            ref.price === attire.price
                        );
                      return (
                        <div key={attire._id} className="purchase-with-refund">
                          <PurchaseCard
                            refundItem={matchingRefund || null}
                            type={attire.attireTag}
                            item={attire.attireTag || "Attire"}
                            amount={
                              attire.totalAmount ||
                              attire.amountPaid ||
                              attire.price ||
                              0
                            }
                            datePaid={attire.paymentDate}
                            deliveryAddress={attire.deliveryAddress}
                            attirePaymentdetails={`${formatCurrency(
                              attire.price
                            )} x ${attire.quantity}`}
                            icon={
                              attire.uri ? (
                                <img
                                  src={attire.uri}
                                  alt={attire.attireTag}
                                  style={{
                                    width: 60,
                                    height: 60,
                                    objectFit: "cover",
                                    borderRadius: 6,
                                  }}
                                />
                              ) : (
                                <AttireIcon />
                              )
                            }
                          />
                        </div>
                      );
                    })}
                </div>

                {!hasPaidForAttire &&
                  hasValidEventDate &&
                  (!refundDeadlineDate || isRefundDeadlineActive) && (
                    <button
                      className="other-buy-button"
                      onClick={() => setModalType("attire")}
                    >
                      Buy
                    </button>
                  )}

                {hasPaidForAttire &&
                  isRefundDeadlineActive &&
                  refundStatus === null && (
                    <button
                      className="other-buy-button"
                      onClick={() => {
                        setRefundStep("confirm");
                      }}
                      disabled={!isRefundDeadlineActive}
                    >
                      Refund
                    </button>
                  )}

                {hasPaidForAttire && normalizedRefundStatus && (
                  <span
                    className={`other-label-button ${
                      !isRefundDeadlineActive
                        ? "refund-deadline-expired"
                        : refundStatus === "pending"
                        ? "pending-refund"
                        : refundStatus === "accepted"
                        ? "approved-refund"
                        : refundStatus === "declined"
                        ? "cancelled-refund"
                        : refundStatus === "paid"
                        ? "paid-refund"
                        : refundStatus === "cancelled"
                        ? "revoked-refund"
                        : ""
                    }`}
                  >
                    {!isRefundDeadlineActive
                      ? "Refund deadline has passed"
                      : refundStatus === "accepted"
                      ? "Refund Requested"
                      : refundStatus === "declined"
                      ? "Refund Cancelled"
                      : refundStatus === "paid"
                      ? "Refund Paid"
                      : refundStatus === "cancelled"
                      ? "Refund Revoked"
                      : refundStatus === "pending"
                      ? "Pending Refund"
                      : ""}
                  </span>
                )}
              </div>
            )}

            {eventData.isCashGiftAccepted && (
              <div className="other-option-row">
                <div className="other-option-description">
                  <div className="option-title-wrapper">
                    <h3 className="other-option-title">Cash Gift (Optional)</h3>
                  </div>
                  <p className="other-option-text">
                    You can donate a monetary gift to support the event or the
                    event host.
                  </p>

                  {hasPaidCashGift && paidCashGift && (
                    <PurchaseCard
                      type="cashGift"
                      item="Cash Gift"
                      amount={paidCashGift.giftAmountPaid}
                      giftDescription={paidCashGift.giftdescription}
                      datePaid={paidCashGift.datePaid || paidCashGift.paidAt}
                      icon={<GiftIcon />}
                    />
                  )}
                </div>
                {!hasPaidCashGift && (
                  <button
                    className="other-donate-button"
                    onClick={() => setModalType("cashGift")}
                  >
                    Gift
                  </button>
                )}
              </div>
            )}

            {eventData.isEntryFeeApplicable && (
              <div className="other-option-row">
                <div className="other-option-description">
                  <div className="option-title-wrapper">
                    <h3 className="other-option-title">
                      Ticket (Entry Fee)
                      {eventData.isEntryFeeApplicable && (
                        <span className="required">*</span>
                      )}
                    </h3>
                  </div>
                  <p className="other-option-text">
                    This event requires a ticket for entry. Proceed to purchase
                    your ticket now!
                  </p>

                  {hasPaidForTicket && paidTicket && (
                    <PurchaseCard
                      type="ticket"
                      item={paidTicket.type || ""}
                      amount={paidTicket.amount || paidTicket.price || 0}
                      datePaid={paidTicket.paidAt || paidTicket.datePaid}
                      icon={<TicketIcon />}
                    />
                  )}
                </div>
                {!hasPaidForTicket && hasValidEventDate && (
                  <button
                    className="other-buy-button"
                    onClick={() => onPurchaseClick("ticket", event.ticketFee)}
                  >
                    {event.ticketFee?.every((t) => parseFloat(t.price) === 0)
                      ? "Claim"
                      : "Buy"}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {modalType === "cashGift" && <CashGiftModal />}

      <AttireModal
        event={event}
        isOpen={modalType === "attire"}
        user={user}
        onClose={() => setModalType(null)}
      />

      {refundStep === "confirm" && (
        <RefundConfirmModal
          onProceed={() => {
            if (eventData?.preferredCurrency === "USD") {
              setRefundStep("selectAttire");
            } else {
              setRefundStep("bank");
            }
          }}
          onClose={() => setRefundStep(null)}
          finalConfirm={false}
          isLoading={false}
        />
      )}

      {refundStep === "bank" && (
        <RefundBankModal
          banks={banks}
          onClose={() => setRefundStep(null)}
          onProceed={(bankDetails) => {
            setRefundPayload((prev) => ({
              ...prev,
              bank: bankDetails,
            }));
            setRefundStep("selectAttire");
          }}
        />
      )}

      {refundStep === "selectAttire" && (
        <RefundAttireModal
          setRefundStep={setRefundStep}
          onProceed={(items) => {
            setRefundPayload((prev) => ({
              ...prev,
              items: { ...refundQuantities },
            }));
            setRefundStep("finalConfirm");
          }}
          onClose={() => setRefundStep(null)}
          invitation={invitation}
          eventData={eventData}
          refundQuantities={refundQuantities}
          setRefundQuantities={setRefundQuantities}
        />
      )}

      {refundStep === "breakdown" && (
        <RefundBreakdownModal
          selectedAttires={invitation.eventAttire.filter(
            (a) => refundQuantities?.[a._id]
          )}
          onUpdateQuantity={(id, qty) =>
            setRefundQuantities((prev) => ({
              ...prev,
              [id]: qty,
            }))
          }
          quantities={refundQuantities}
          refundQuantities={refundQuantities}
          onClose={() => setRefundStep("selectAttire")}
          onProceed={() => {
            setRefundStep("finalConfirm");
          }}
          eventData={eventData}
        />
      )}

      {refundStep === "finalConfirm" && (
        <RefundConfirmModal
          onClose={() => setRefundStep("selectAttire")}
          onProceed={handleRefundRequest}
          finalConfirm={true}
          isLoading={isSubmittingRefund}
        />
      )}

      {refundStep === "success" && (
        <RefundSuccessModal
          onClose={() => setRefundStep(null)}
          currency={eventData?.preferredCurrency}
        />
      )}

      {refundStep === "failed" && (
        <RefundFailedModal
          message={refundError}
          onClose={() => setRefundStep(null)}
        />
      )}

      {isPaymentSummaryModalOpen && paymentSummaryData && (
        <PaymentSummaryModal
          isOpen={isPaymentSummaryModalOpen}
          onClose={() => {
            setIsPaymentSummaryModalOpen(false);
            setPaymentSummaryData(null);
          }}
          onBack={() => {
            setIsPaymentSummaryModalOpen(false);
            if (paymentSummaryData.type === "cashGift") {
              setModalType("cashGift");
            } else if (paymentSummaryData.type === "attire") {
              setModalType("attire");
            }
          }}
          event={eventData}
          selectedOptions={paymentSummaryData.selectedOptions}
          deliveryAddress={paymentSummaryData.deliveryAddress}
          quantities={paymentSummaryData.quantities}
          isAttirePurchase={paymentSummaryData.isAttirePurchase}
          applyServiceCharge={paymentSummaryData.applyServiceCharge}
          user={user}
        />
      )}
    </>
  );
};

export default OtherPurchaseOptions;
